import { useState } from 'react';
import { buildQueryAst } from '../../../../common';
import { astToTokens } from './helpers';
import {
  QueryBuilderAstNodeType,
  QueryBuilderEntityConfigType,
  QueryBuilderOperatorType,
  QueryBuilderTokenType,
  QueryBuilderTypingCommitActionType,
} from '../../../../types';
import {
  adjustEditTokenIndexAfterRemove,
  adjustEditTokenIndexAfterReorder,
  applyConditionValueSwap,
  applyOperatorSwap,
  removeTokenAtIndex,
  reorderTokensAtIndex,
  startConditionValueSwap,
} from './helpers';

type UseQueryBuilderTokensArgsType = {
  entities: QueryBuilderEntityConfigType[];
  onChange: (ast: ReturnType<typeof buildQueryAst>) => void;
  onCommit: () => void;
};

type UseQueryBuilderTokensReturnType = {
  tokens: QueryBuilderTokenType[];
  editTokenIndex: number | null;
  pendingEntity: QueryBuilderEntityConfigType | null;
  isDragDisabled: boolean;
  handleTypingCommitAction: (
    action: QueryBuilderTypingCommitActionType
  ) => void;
  handleOpenBracket: () => void;
  handleCloseBracket: () => void;
  handleRemoveTokenAtIndex: (index: number) => void;
  handleReorderTokens: (fromIndex: number, toIndex: number) => void;
  handleConditionPillClick: (tokenIndex: number) => void;
  handleClearAll: () => void;
  loadAst: (ast: QueryBuilderAstNodeType) => void;
};

export const useQueryBuilderTokens = ({
  entities,
  onChange,
  onCommit,
}: UseQueryBuilderTokensArgsType): UseQueryBuilderTokensReturnType => {
  const [tokens, setTokens] = useState<QueryBuilderTokenType[]>([]);
  const [editTokenIndex, setEditTokenIndex] = useState<number | null>(null);

  const lastToken = tokens[tokens.length - 1];
  const activeEntityToken =
    editTokenIndex !== null ? tokens[editTokenIndex] : lastToken;
  const pendingEntityKey =
    activeEntityToken?.type === 'entity' ? activeEntityToken.entityKey : null;
  const pendingEntity =
    entities.find((entity) => entity.key === pendingEntityKey) ?? null;

  const updateTokens = (nextTokens: QueryBuilderTokenType[]) => {
    setTokens(nextTokens);
  };

  const handleEntitySelect = (entityKey: string) => {
    updateTokens([...tokens, { type: 'entity', entityKey }]);
    onCommit();
  };

  const handleValueSelect = (value: string) => {
    if (editTokenIndex !== null) {
      const nextTokens = applyConditionValueSwap(tokens, editTokenIndex, value);

      if (!nextTokens) {
        return;
      }

      setEditTokenIndex(null);
      updateTokens(nextTokens);
      onCommit();
      return;
    }

    if (!pendingEntityKey) {
      return;
    }

    updateTokens([
      ...tokens.slice(0, -1),
      { type: 'entityValue', entityKey: pendingEntityKey, value },
    ]);
    onCommit();
  };

  const handleOperatorSelect = (operator: QueryBuilderOperatorType) => {
    if (editTokenIndex !== null) {
      const nextTokens = applyOperatorSwap(tokens, editTokenIndex, operator);

      if (!nextTokens) {
        return;
      }

      setEditTokenIndex(null);
      updateTokens(nextTokens);
      onCommit();
      return;
    }

    updateTokens([...tokens, { type: 'operator', operator }]);
    onCommit();
  };

  const handleOpenBracket = () => {
    updateTokens([...tokens, { type: 'bracket', bracket: 'OPEN' }]);
    onCommit();
  };

  const handleCloseBracket = () => {
    updateTokens([...tokens, { type: 'bracket', bracket: 'CLOSE' }]);
    onCommit();
  };

  const handleConditionSelect = (entityKey: string, value: string) => {
    updateTokens([...tokens, { type: 'entityValue', entityKey, value }]);
    onCommit();
  };

  const handleTypingCommitAction = (
    action: QueryBuilderTypingCommitActionType
  ) => {
    switch (action.type) {
      case 'entity':
        handleEntitySelect(action.entityKey);
        break;
      case 'entityValue':
        handleConditionSelect(action.entityKey, action.value);
        break;
      case 'value':
        handleValueSelect(action.value);
        break;
      case 'operator':
        handleOperatorSelect(action.operator);
        break;
      case 'openBracket':
        handleOpenBracket();
        break;
      case 'closeBracket':
        handleCloseBracket();
        break;
      default:
        break;
    }
  };

  const handleRemoveTokenAtIndex = (index: number) => {
    const nextTokens = removeTokenAtIndex(tokens, index);

    setEditTokenIndex(adjustEditTokenIndexAfterRemove(editTokenIndex, index));
    updateTokens(nextTokens);
    onCommit();
  };

  const handleReorderTokens = (fromIndex: number, toIndex: number) => {
    const nextTokens = reorderTokensAtIndex(tokens, fromIndex, toIndex);

    if (nextTokens === tokens) {
      return;
    }

    setEditTokenIndex(
      adjustEditTokenIndexAfterReorder(editTokenIndex, fromIndex, toIndex)
    );
    updateTokens(nextTokens);
    onCommit();
  };

  const handleConditionPillClick = (tokenIndex: number) => {
    const token = tokens[tokenIndex];

    if (token?.type === 'operator') {
      setEditTokenIndex(tokenIndex);
      onCommit();
      return;
    }

    const nextTokens = startConditionValueSwap(tokens, tokenIndex);

    if (!nextTokens) {
      return;
    }

    setEditTokenIndex(tokenIndex);
    updateTokens(nextTokens);
    onCommit();
  };

  const handleClearAll = () => {
    setTokens([]);
    setEditTokenIndex(null);
    onCommit();
    onChange(null);
  };

  const loadAst = (ast: QueryBuilderAstNodeType) => {
    const nextTokens = astToTokens(ast);

    setTokens(nextTokens);
    setEditTokenIndex(null);
    onCommit();
    onChange(ast);
  };

  return {
    tokens,
    editTokenIndex,
    pendingEntity,
    isDragDisabled: editTokenIndex !== null,
    handleTypingCommitAction,
    handleOpenBracket,
    handleCloseBracket,
    handleRemoveTokenAtIndex,
    handleReorderTokens,
    handleConditionPillClick,
    handleClearAll,
    loadAst,
  };
};
